import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { fetchProfile, updateProfile } from "../api/user";
import { subscribePush, unsubscribePush } from "../api/push";
import { isPushSupported, needsHomeScreenInstall, subscribeToPush, unsubscribeFromPush } from "../utils/push";
import { ABILITY_LEVELS } from "../constants/abilityLevels";
import { HOME_LOCATIONS } from "../constants/homeLocations";
import { PASS_TYPES } from "../constants/passTypes";

function PowderAlertsSection({ userId, profile, onProfileChanged }) {
  const [error, setError] = useState(null);

  const enable = useMutation({
    mutationFn: async () => {
      const subscription = await subscribeToPush();
      await subscribePush(subscription);
      await updateProfile(userId, { conditionAlerts: true });
    },
    onSuccess: () => {
      setError(null);
      onProfileChanged();
    },
    onError: (err) => setError(err?.message || "Couldn't enable alerts — try again."),
  });

  const disable = useMutation({
    mutationFn: async () => {
      await unsubscribeFromPush();
      await unsubscribePush();
      await updateProfile(userId, { conditionAlerts: false });
    },
    onSuccess: () => {
      setError(null);
      onProfileChanged();
    },
  });

  return (
    <div className="glass">
      <div className="resort-name">Powder alerts</div>
      <p className="sec-dek">A push notification when a great day is coming this weekend — at most weekly.</p>

      {!isPushSupported() ? (
        <p className="sec-dek">Not supported in this browser.</p>
      ) : needsHomeScreenInstall() ? (
        <p className="sec-dek">
          On iPhone, push only works from an installed app: Share → Add to Home Screen, then open Pow Horizon
          from your home screen and try again.
        </p>
      ) : profile?.conditionAlerts ? (
        <button className="pill on" onClick={() => disable.mutate()} disabled={disable.isPending}>
          Alerts on — tap to turn off
        </button>
      ) : (
        <button className="btn-accent" onClick={() => enable.mutate()} disabled={enable.isPending}>
          {enable.isPending ? "Enabling…" : "Enable powder alerts"}
        </button>
      )}
      {error && <p className="badge-warn">{error}</p>}
    </div>
  );
}

function mutationErrorMessage(err) {
  if (err?.response?.status === 401) return "Your session expired — sign out and back in, then try again.";
  return err?.response?.data?.error || err?.message || "Couldn't save — try again.";
}

export function ProfileOverlay({ onClose }) {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState(null);

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.userId],
    queryFn: () => fetchProfile(user.userId),
    enabled: !!user?.userId,
  });

  const invalidateProfile = () => queryClient.invalidateQueries({ queryKey: ["profile", user?.userId] });
  const onSaveSuccess = () => {
    setSaveError(null);
    invalidateProfile();
  };
  const onSaveError = (err) => setSaveError(mutationErrorMessage(err));

  const mutation = useMutation({
    mutationFn: (abilityLevel) => updateProfile(user.userId, { abilityLevel }),
    onSuccess: onSaveSuccess,
    onError: onSaveError,
  });

  const homeLocationMutation = useMutation({
    mutationFn: (homeLocation) => updateProfile(user.userId, { homeLocation }),
    onSuccess: onSaveSuccess,
    onError: onSaveError,
  });

  const passTypeMutation = useMutation({
    mutationFn: (passType) => updateProfile(user.userId, { passType }),
    onSuccess: onSaveSuccess,
    onError: onSaveError,
  });

  return (
    <div className="sheet">
      <button className="auth-close-btn" onClick={onClose} aria-label="Close">
        ×
      </button>
      <h2>Profile</h2>
      <p>{user?.signInDetails?.loginId || user?.username}</p>
      {saveError && <p className="badge-warn">{saveError}</p>}

      <div className="glass">
        <div className="resort-name">Home base</div>
        <p className="sec-dek">
          Where you're driving up from — Tahoe locals skip the drive-time stuff, everyone else gets it front
          and center.
        </p>
        <div className="pill-row">
          {HOME_LOCATIONS.map((loc) => (
            <button
              key={loc.id}
              className={`pill${profile?.homeLocation === loc.id ? " on" : ""}`}
              onClick={() => homeLocationMutation.mutate(loc.id)}
              disabled={homeLocationMutation.isPending}
            >
              {loc.label}
            </button>
          ))}
        </div>
      </div>

      <div className="glass">
        <div className="resort-name">Season pass</div>
        <p className="sec-dek">Highlights the resorts covered by your pass on the Conditions board.</p>
        <div className="pill-row">
          {PASS_TYPES.map((pass) => (
            <button
              key={pass.id}
              className={`pill${profile?.passType === pass.id ? " on" : ""}`}
              onClick={() => passTypeMutation.mutate(pass.id)}
              disabled={passTypeMutation.isPending}
            >
              {pass.label}
            </button>
          ))}
        </div>
      </div>

      <div className="glass">
        <div className="resort-name">Ski/ride level</div>
        <p className="sec-dek">Used to match you with carpool posts and buddies at your level.</p>
        <div className="pill-row">
          {ABILITY_LEVELS.map((level) => (
            <button
              key={level}
              className={`pill${profile?.abilityLevel === level ? " on" : ""}`}
              onClick={() => mutation.mutate(level)}
              disabled={mutation.isPending}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {user?.userId && <PowderAlertsSection userId={user.userId} profile={profile} onProfileChanged={invalidateProfile} />}

      <button className="btn-accent" onClick={signOut}>
        Sign out
      </button>
    </div>
  );
}
